import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'
import { reportActorKey, useCurrentUser } from './hooks'
import { fresh, publicSeriesClient } from './series-publication'
import { liveApiClient, liveQueryOptions } from './live-query'

/** Anonymous read through the cookie-free transport; never the staff client. */
export function usePublicShowResults(slug: string) {
  return useQuery({
    queryKey: ['public-show', slug],
    queryFn: ({ signal }) => publicSeriesClient.getPublicShowResults(slug, signal),
    enabled: Boolean(slug),
    ...fresh,
  })
}
export function useShowResultsRelease(showId: string, options?: { live?: boolean }) {
  const user = useCurrentUser()
  return useQuery({
    queryKey: ['show-results-release', showId, ...reportActorKey(user.data)],
    queryFn: ({ signal }) =>
      (options?.live ? liveApiClient : apiClient).getShowResultsRelease(showId, signal),
    enabled: Boolean(user.data) && Boolean(showId),
    ...fresh,
    ...(options?.live ? liveQueryOptions : {}),
  })
}
export function useShowResultsReleaseMutation(showId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      action:
        | { kind: 'preview' }
        | { kind: 'release'; token: string }
        | { kind: 'withdraw'; generation: number }
    ) => {
      if (action.kind === 'preview') return apiClient.previewShowResultsRelease(showId)
      if (action.kind === 'release') return apiClient.releaseShowResults(showId, action.token)
      return apiClient.withdrawShowResults(showId, action.generation)
    },
    onSettled: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['show-results-release', showId] }),
        qc.invalidateQueries({ queryKey: ['public-show'] }),
        qc.invalidateQueries({ queryKey: ['public-page-metadata'] }),
        qc.invalidateQueries({ queryKey: ['public-atlas'] }),
        qc.invalidateQueries({ queryKey: ['public-classes'] }),
        qc.invalidateQueries({ queryKey: ['public-championships'] }),
      ])
    },
  })
}
